import { Injectable } from '@angular/core';

/**
 * Selector matching every DOM node that can receive keyboard focus.
 * Excludes disabled / inert / tabindex=-1 nodes.
 */
const FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
    '[contenteditable="true"]',
].join(',');

/**
 * Focus management for modal-like UIs.
 *
 * `trap(container, { onEscape })` wraps a container with:
 * - Tab / Shift+Tab cycling inside the container (keyboard users never escape)
 * - Optional Escape handler
 * - Initial focus on the first focusable element (or the container itself)
 * - Restoration of the previously focused element when released
 *
 * Usage:
 * ```
 * private cleanup?: () => void;
 * ngAfterViewInit() {
 *     this.cleanup = this.focusTrap.trap(this.modal.nativeElement, {
 *         onEscape: () => this.close(),
 *     });
 * }
 * ngOnDestroy() { this.cleanup?.(); }
 * ```
 */
@Injectable({ providedIn: 'root' })
export class FocusTrapService {
    trap(
        container: HTMLElement,
        opts: { onEscape?: () => void } = {},
    ): () => void {
        const previouslyFocused = document.activeElement as HTMLElement | null;

        const focus = (el: HTMLElement | null) =>
            el?.focus({ preventScroll: true });

        // Initial focus: first focusable child, or the container (made focusable).
        const initialTargets = this.getFocusable(container);
        if (initialTargets.length > 0) {
            focus(initialTargets[0]);
        } else {
            if (!container.hasAttribute('tabindex')) {
                container.setAttribute('tabindex', '-1');
            }
            focus(container);
        }

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && opts.onEscape) {
                event.preventDefault();
                opts.onEscape();
                return;
            }

            if (event.key !== 'Tab') return;

            const focusable = this.getFocusable(container);
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const active = document.activeElement as HTMLElement;

            if (event.shiftKey && active === first) {
                event.preventDefault();
                focus(last);
            } else if (!event.shiftKey && active === last) {
                event.preventDefault();
                focus(first);
            }
        };

        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.removeEventListener('keydown', onKeyDown);
            focus(previouslyFocused);
        };
    }

    private getFocusable(root: HTMLElement): HTMLElement[] {
        return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
            (el) => el.getAttribute('aria-hidden') !== 'true',
        );
    }
}
